import { CheckOutlined } from '@ant-design/icons'
import type { HomeDto } from '@lmc/ts-common'
import { Button, Form, PageHeader } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Form as AntdForm, Tabs } from 'antd'
import { useWatch } from 'antd/lib/form/Form'
import { showToast } from 'common/utils'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import useFeatureFlags from 'hooks/useFeatureFlags'
import type { CarerEmailWithName } from 'hooks/useGroupedOptions'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Navigate } from 'react-router-dom'
import styled from 'styled-components'
import { PageWrapper } from '../components'
import { handleOnFinishFailed } from '../formComponents/utils'
import useUpdateWarningsSettings from '../hooks/useUpdateWarningsSettings'
import NotificationSettingsTab from './components/NotificationSettingsTab'
import SettingsPaywall from './components/SettingsPaywall'
import ToleranceSettingsTab from './components/ToleranceSettingsTab'

type WarningsSettingsForm = {
  notYetClockedIn: number
  clockInEarly: number
  clockInLate: number
  clockOutEarly: number
  clockOutLate: number
  missed: number
  durationMismatched: number
  // Notification settings
  enableNotifications?: boolean
  notYetClockedInNotification?: boolean
  clockInEarlyNotification?: boolean
  clockInLateNotification?: boolean
  clockOutEarlyNotification?: boolean
  clockOutLateNotification?: boolean
  missedNotification?: boolean
  durationMismatchedNotification?: boolean
  emailRecipients?: CarerEmailWithName[]
}

const WarningsSettingsScreen = () => {
  const { t } = useTranslation()
  const [form] = AntdForm.useForm()
  const [isDirty, setIsDirty] = useState(false)
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('tolerance')
  const lastSavedNotificationsSwitchRef = useRef<boolean | undefined>(undefined)
  const updateWarningsSettings = useUpdateWarningsSettings()
  const sendMixpanelEvent = useMixpanelEvent()
  const { data: flags, isLoading } = useFeatureFlags({
    flagNames: [
      'rostering_tolerance_settings',
      'rota_warnings_email_notifications'
    ]
  })

  const flagsRosteringToleranceSettings =
    flags?.rostering_tolerance_settings?.enabled

  const flagsRotaWarningsEmailNotifications =
    flags?.rota_warnings_email_notifications?.enabled

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )

  const warningsNotifications =
    (home.rotaSettings as any)?.warningsNotifications || {}

  // Enable notifications if any notification is enabled or if email recipients are set
  const hasAnyNotificationEnabled =
    warningsNotifications.notYetClockedIn ||
    warningsNotifications.clockInEarly ||
    warningsNotifications.clockInLate ||
    warningsNotifications.clockOutEarly ||
    warningsNotifications.clockOutLate ||
    warningsNotifications.missed ||
    warningsNotifications.durationMismatched ||
    (warningsNotifications.emailRecipients &&
      warningsNotifications.emailRecipients.length > 0)

  const initialValues = {
    ...home.rotaSettings?.warningsTolerance,
    enableNotifications: hasAnyNotificationEnabled ?? false,
    notYetClockedInNotification: warningsNotifications.notYetClockedIn ?? false,
    clockInEarlyNotification: warningsNotifications.clockInEarly ?? false,
    clockInLateNotification: warningsNotifications.clockInLate ?? false,
    clockOutEarlyNotification: warningsNotifications.clockOutEarly ?? false,
    clockOutLateNotification: warningsNotifications.clockOutLate ?? false,
    missedNotification: warningsNotifications.missed ?? false,
    durationMismatchedNotification:
      warningsNotifications.durationMismatched ?? false,
    emailRecipients: warningsNotifications.emailRecipients || []
  }

  useWatch('warnings', form)

  const handleOnFinish = async (values: WarningsSettingsForm) => {
    try {
      setIsUpdateLoading(true)
      setIsDirty(false)

      await updateWarningsSettings({
        warningsTolerance: {
          notYetClockedIn: Number(values.notYetClockedIn),
          clockInEarly: Number(values.clockInEarly),
          clockInLate: Number(values.clockInLate),
          clockOutEarly: Number(values.clockOutEarly),
          clockOutLate: Number(values.clockOutLate),
          missed: Number(values.missed),
          durationMismatched: Number(values.durationMismatched)
        } as any,
        warningsNotifications: values.enableNotifications
          ? {
              notYetClockedIn: values.notYetClockedInNotification ?? false,
              clockInEarly: values.clockInEarlyNotification ?? false,
              clockInLate: values.clockInLateNotification ?? false,
              clockOutEarly: values.clockOutEarlyNotification ?? false,
              clockOutLate: values.clockOutLateNotification ?? false,
              missed: values.missedNotification ?? false,
              durationMismatched:
                values.durationMismatchedNotification ?? false,
              emailRecipients: values.emailRecipients || []
            }
          : {
              notYetClockedIn: false,
              clockInEarly: false,
              clockInLate: false,
              clockOutEarly: false,
              clockOutLate: false,
              missed: false,
              durationMismatched: false,
              emailRecipients: []
            }
      } as any)

      if (flagsRotaWarningsEmailNotifications) {
        const notificationsEnabledBySwitch = values.enableNotifications ?? false
        const previousSwitchState =
          lastSavedNotificationsSwitchRef.current ?? hasAnyNotificationEnabled
        const switchWasToggled =
          previousSwitchState !== notificationsEnabledBySwitch

        if (switchWasToggled) {
          const warningKeys = [
            'notYetClockedIn',
            'clockInEarly',
            'clockInLate',
            'clockOutEarly',
            'clockOutLate',
            'missed',
            'durationMismatched'
          ] as const
          const notificationKeyMap: Record<
            (typeof warningKeys)[number],
            keyof WarningsSettingsForm
          > = {
            notYetClockedIn: 'notYetClockedInNotification',
            clockInEarly: 'clockInEarlyNotification',
            clockInLate: 'clockInLateNotification',
            clockOutEarly: 'clockOutEarlyNotification',
            clockOutLate: 'clockOutLateNotification',
            missed: 'missedNotification',
            durationMismatched: 'durationMismatchedNotification'
          }
          const enabledWarnings = warningKeys.filter(
            (key) => values[notificationKeyMap[key]] === true
          )
          const teamMembersCount = values.emailRecipients?.length ?? 0

          sendMixpanelEvent(MixpanelEvents.WARNING_NOTIFICATIONS_SET, {
            notificationsEnabled: notificationsEnabledBySwitch,
            allWarningsDisabledBySwitch: !notificationsEnabledBySwitch,
            enabledWarnings: notificationsEnabledBySwitch
              ? enabledWarnings
              : [],
            teamMembersCount
          })
        }

        lastSavedNotificationsSwitchRef.current = notificationsEnabledBySwitch
      }

      setIsUpdateLoading(false)
    } catch (err: any) {
      setIsUpdateLoading(false)
    }
    setIsDirty(false)
  }

  const handleFormValuesChange = (
    changedValues: Partial<WarningsSettingsForm>,
    _allValues: WarningsSettingsForm
  ) => {
    if (changedValues?.enableNotifications === true) {
      form.setFieldValue('notYetClockedInNotification', true)
      form.setFieldValue('missedNotification', true)
    }
    if (changedValues && Object.keys(changedValues).length > 0) {
      setIsDirty(true)
    }
  }

  if (!flagsRosteringToleranceSettings && !isLoading) return <Navigate to="/" />

  return (
    <>
      <PageHeader
        title={t('WARNINGS')}
        subtitle={t('WARNINGS_SETTINGS_INFO')}
        sticky
      />
      <PageWrapper>
        <SettingsPaywall startingText={t('WARNINGS_IS')}>
          <Form<WarningsSettingsForm>
            form={form}
            initialValues={initialValues}
            onValuesChange={handleFormValuesChange}
            onFinish={handleOnFinish}
            onFinishFailed={({ errorFields }) => {
              handleOnFinishFailed(errorFields)
            }}
          >
            <FormContent>
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                  {
                    key: 'tolerance',
                    label: <Text>{t('TOLERANCE_SETTINGS')}</Text>,
                    children: <ToleranceSettingsTab />
                  },
                  ...(flagsRotaWarningsEmailNotifications
                    ? [
                        {
                          key: 'notifications',
                          label: <Text>{t('NOTIFICATIONS')}</Text>,
                          children: <NotificationSettingsTab />
                        }
                      ]
                    : [])
                ]}
              />
            </FormContent>
            <FixedFooter>
              <Button
                isLoading={isUpdateLoading}
                variant="primary"
                type="button"
                disabled={!isDirty}
                onClick={form.submit}
              >
                <CheckOutlined />
                {t('SAVE_CHANGES')}
              </Button>
            </FixedFooter>
          </Form>
        </SettingsPaywall>
      </PageWrapper>
      <NavigationModal
        canNavigate={!isDirty}
        modalContentText={t('ROTA_SETTINGS_NAVIGATION_MODAL_CONTENT')}
      />
    </>
  )
}

const FormContent = styled.div`
  padding-bottom: 60px;
`

const FixedFooter = styled.div.attrs({
  className: 'lmc-fixed-footer' // I don't like this, we need to make a SB component after the removal of the keystone legacy file
})`
  border: none;
  display: flex;
  justify-content: flex-end;
  width: calc(100vw - 290px);
  @media (max-width: 768px) {
    width: 100vw;
  }
`

export default WarningsSettingsScreen
